import type {
  DetailedOptimizationSolution,
  OptimizationRunRecord,
  ScenarioOptimizationResult,
} from './types';

const CUBIC_METRES_PER_OIL_BARREL = 0.158987294928;
const barrelNumber = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

type NameResolver = (id: string) => string;
type ScenarioDefinition =
  OptimizationRunRecord['request']['network_project']['scenarios'][number];

function unitSite(
  solution: DetailedOptimizationSolution,
  unitId: string,
): string | null {
  for (const bank of solution.physical_pump_banks) {
    if (bank.stages.some((stage) => stage.includes(unitId))) {
      return bank.candidate_site_id;
    }
  }
  return null;
}

function bankUnits(bank: DetailedOptimizationSolution['physical_pump_banks'][number]) {
  return [...bank.stages.flat()].sort().join('|');
}

export function describeFeasibleChange(
  baseline: DetailedOptimizationSolution,
  alternative: DetailedOptimizationSolution,
  unitName: NameResolver,
  siteName: NameResolver,
  scenarioName: NameResolver,
): string | null {
  const baselineIds = new Set(
    baseline.physical_units.map(({ fleet_unit: unit }) => unit.id),
  );
  const alternativeIds = new Set(
    alternative.physical_units.map(({ fleet_unit: unit }) => unit.id),
  );
  const added = [...alternativeIds].filter((id) => !baselineIds.has(id));
  const removed = [...baselineIds].filter((id) => !alternativeIds.has(id));

  if (added.length === 1 && removed.length === 1) {
    const siteId = unitSite(alternative, added[0]!);
    return `Use ${unitName(added[0]!)}${
      siteId === null ? '' : ` at ${siteName(siteId)}`
    } instead of ${unitName(removed[0]!)}.`;
  }
  if (added.length === 1 && removed.length === 0) {
    const siteId = unitSite(alternative, added[0]!);
    return `Add ${unitName(added[0]!)}${
      siteId === null ? '' : ` at ${siteName(siteId)}`
    }.`;
  }
  if (removed.length === 1 && added.length === 0) {
    return `Remove ${unitName(removed[0]!)}.`;
  }

  for (const unitId of [...alternativeIds].filter((id) =>
    baselineIds.has(id),
  )) {
    const beforeSite = unitSite(baseline, unitId);
    const afterSite = unitSite(alternative, unitId);
    if (
      beforeSite !== null &&
      afterSite !== null &&
      beforeSite !== afterSite
    ) {
      return `Use ${siteName(afterSite)} instead of ${siteName(
        beforeSite,
      )} for ${unitName(unitId)}.`;
    }
  }

  for (const bank of alternative.physical_pump_banks) {
    const before = baseline.physical_pump_banks.find(
      (candidate) => bankUnits(candidate) === bankUnits(bank),
    );
    if (before !== undefined && before.arrangement !== bank.arrangement) {
      return `Use ${bank.arrangement} instead of ${before.arrangement} at ${siteName(
        bank.candidate_site_id,
      )}.`;
    }
  }

  for (const scenario of alternative.scenarios) {
    const beforeScenario = baseline.scenarios.find(
      (candidate) => candidate.scenario_id === scenario.scenario_id,
    );
    if (beforeScenario === undefined) continue;
    for (const setting of scenario.unit_settings) {
      const before = beforeScenario.unit_settings.find(
        (candidate) => candidate.fleet_unit_id === setting.fleet_unit_id,
      );
      if (before === undefined) continue;
      if (
        before.engine_speed_rpm !== setting.engine_speed_rpm &&
        before.engine_speed_rpm !== null &&
        setting.engine_speed_rpm !== null
      ) {
        return `Run ${unitName(setting.fleet_unit_id)} at ${setting.engine_speed_rpm.toLocaleString(
          'en-US',
        )} RPM instead of ${before.engine_speed_rpm.toLocaleString(
          'en-US',
        )} RPM in ${scenarioName(scenario.scenario_id)}.`;
      }
      if (before.enabled !== setting.enabled) {
        return `${setting.enabled ? 'Enable' : 'Disable'} ${unitName(
          setting.fleet_unit_id,
        )} in ${scenarioName(scenario.scenario_id)}.`;
      }
    }
  }

  if (
    baseline.objective.physical_pump_count !==
    alternative.objective.physical_pump_count
  ) {
    return `Use ${alternative.objective.physical_pump_count} pumps instead of ${baseline.objective.physical_pump_count}.`;
  }
  if (
    baseline.objective.occupied_site_count !==
    alternative.objective.occupied_site_count
  ) {
    return `Use ${alternative.objective.occupied_site_count} sites instead of ${baseline.objective.occupied_site_count}.`;
  }
  return null;
}

export function deliveredBarrelSummary(
  scenario: ScenarioOptimizationResult,
  definition: ScenarioDefinition | undefined,
): string | null {
  const deliveries = scenario.simulation_result.sink_deliveries ?? [];
  if (deliveries.length === 0) return null;
  const deliveredFlow = deliveries.reduce(
    (total, delivery) => total + delivery.delivered_flow_m3_s,
    0,
  );
  const barrelsPerSecond = deliveredFlow / CUBIC_METRES_PER_OIL_BARREL;
  const parts = [
    `${barrelNumber.format(barrelsPerSecond * 60)} BPM`,
    `${barrelNumber.format(barrelsPerSecond * 3_600)} BPH`,
  ];
  if (deliveries.length !== 1) return parts.join(' · ');
  const sink = definition?.sinks?.find(
    (candidate) => candidate.sink_node_id === deliveries[0]!.sink_node_id,
  );
  const requirement = sink?.job_requirement;
  if (
    typeof requirement?.delivery_period_s === 'number' &&
    typeof requirement.expected_uptime_fraction === 'number'
  ) {
    const total =
      barrelsPerSecond *
      requirement.delivery_period_s *
      requirement.expected_uptime_fraction;
    const days = requirement.delivery_period_s / 86_400;
    const period =
      Number.isInteger(days) && days >= 1
        ? `${days.toLocaleString('en-US')} ${days === 1 ? 'day' : 'days'}`
        : `${barrelNumber.format(requirement.delivery_period_s / 3_600)} hours`;
    parts.push(`${barrelNumber.format(total)} barrels / ${period}`);
  }
  return parts.join(' · ');
}
